import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import usePersistedColumnVisibility from 'shared/hooks/usePersistedColumnVisibility';
import { tokens } from 'shared/theme';
import useServiceCatalogService from 'shared/api/services/serviceCatalogService';
import AddServiceDialog from './addServiceDialog';
import EditServiceDialog from './EditServiceDialog';
import DeleteServiceDialog from './deleteServiceDialog';
import { buildGridToolbarExportProps } from 'shared/utils/tableExportFileName';

const STATUS_FILTER_OPTIONS = ['', 'ACTIVE', 'DISABLED', 'PENDING', 'DEPLOYED', 'EXPIRED'];

const formatDateCell = (v) => {
  if (v == null || v === '') return '';
  if (Array.isArray(v) && v.length >= 3) {
    const [y, m, d] = v;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  const s = String(v);
  return s.length >= 10 && s.includes('-') ? s.slice(0, 10) : s;
};

const TenantServicesPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { columnVisibilityModel, onColumnVisibilityModelChange } =
    usePersistedColumnVisibility('platform.tenant-services');
  const {
    services,
    totalRecords,
    isLoading,
    fetchServices,
    createService,
    updateService,
    deleteService,
  } = useServiceCatalogService();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    fetchServices(
      paginationModel.page,
      paginationModel.pageSize,
      statusFilter || null,
      searchApplied || null
    );
  }, [
    fetchServices,
    paginationModel.page,
    paginationModel.pageSize,
    statusFilter,
    searchApplied,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const handleEdit = useCallback((row) => setEditRow(row), []);
  const handleDeleteClick = useCallback((row) => setDeleteTarget(row), []);

  const rows = useMemo(
    () =>
      services.map((s) => ({
        id: s.id,
        serviceName: s.serviceName ?? '',
        serviceCode: s.serviceCode ?? '',
        serviceDescription: s.serviceDescription ?? '',
        numberOfUsers: s.numberOfUsers,
        startDate: formatDateCell(s.startDate),
        endDate: formatDateCell(s.endDate),
        status: s.status ?? '',
      })),
    [services]
  );

  const columns = useMemo(
    () => [
      { field: 'serviceName', headerName: 'Name', flex: 0.9, minWidth: 140 },
      { field: 'serviceCode', headerName: 'Code', flex: 0.5, minWidth: 110 },
      {
        field: 'serviceDescription',
        headerName: 'Description',
        flex: 1.1,
        minWidth: 160,
        renderCell: ({ value }) => {
          if (value == null || value === '') {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }
          const t = String(value);
          const short = t.length > 80 ? `${t.slice(0, 80)}…` : t;
          return (
            <Typography variant="body2" noWrap title={t}>
              {short}
            </Typography>
          );
        },
      },
      {
        field: 'numberOfUsers',
        headerName: 'Users',
        width: 90,
        renderCell: ({ value }) =>
          value == null || value === '' ? (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ) : (
            <Typography variant="body2">{value}</Typography>
          ),
      },
      { field: 'startDate', headerName: 'Start', width: 120 },
      { field: 'endDate', headerName: 'End', width: 120 },
      { field: 'status', headerName: 'Status', width: 110 },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box display="flex" alignItems="center" gap={0.25}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEdit(row)}
                aria-label="Edit service"
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteClick(row)}
                aria-label="Delete service"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [handleEdit, handleDeleteClick]
  );

  return (
    <Box m="20px">
      <AddServiceDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={load}
        createService={createService}
      />
      <EditServiceDialog
        open={Boolean(editRow)}
        onClose={() => setEditRow(null)}
        onSuccess={load}
        row={editRow}
        updateService={updateService}
      />
      <DeleteServiceDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onSuccess={load}
        row={deleteTarget}
        deleteService={deleteService}
      />
      <Header title="Services" subtitle="Central service catalog" />
      <Box
        mt={2}
        mb={2}
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
          <TextField
            size="small"
            label="Search"
            placeholder="Name, code, description"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSearchApplied(searchInput.trim());
                setPaginationModel((m) => ({ ...m, page: 0 }));
              }
            }}
            sx={{ minWidth: 200 }}
          />
        
          
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setCreateOpen(true)}
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
          >
            Add service
          </Button>
        </Box>
      </Box>
      <Box mt={0} position="relative">
        {isLoading && (
          <Box
            position="absolute"
            zIndex={2}
            left="50%"
            top="40%"
            sx={{ transform: 'translate(-50%, -50%)' }}
          >
            <LoadingSpinner />
          </Box>
        )}
        <Box
          height="72vh"
          sx={{
            opacity: isLoading ? 0.4 : 1,
            pointerEvents: isLoading ? 'none' : 'auto',
            '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
              color: `${colors.grey[100]} !important`,
            },
          }}
        >
          <DataGrid
            disableRowSelectionOnClick
            rows={rows}
            columns={columns}
            rowCount={totalRecords}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: buildGridToolbarExportProps({
                title: 'Services',
                tableId: 'platform.services',
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              }),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TenantServicesPage;
